import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChanges } from '@angular/core';
import {Router} from '@angular/router';
import {PersonLogin} from '../person/person-login';
import {SelectionItem} from '../selections/selection-item';
import {CustomerPolicyService} from '../services/customer-policy-service';
import {OrganizationService} from '../services/organization-service';
import {PersonLoginService} from '../services/person-login-service';
import {SelectionService} from '../services/selection-service';
import {CustomerPolicy} from './customer-policy';
import { NgClass, DatePipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'customer-policies',
    templateUrl: './customer-policies.component.html',
    styleUrls: ['./customer-policies.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, MatButton, MatIcon, NgClass, DatePipe]
})
export class CustomerPoliciesComponent implements OnInit, OnChanges {
  private readonly personLoginService = inject(PersonLoginService);
  private readonly policyService = inject(CustomerPolicyService);
  private readonly organizationService = inject(OrganizationService);
  private readonly router = inject(Router);
  private readonly selectionService = inject(SelectionService);


  public personLogin!: PersonLogin;
  readonly organizationId = input.required<string>({ alias: 'organization-id' });
  readonly policies = signal<CustomerPolicy[]>(undefined!);
  public selectedPolicy!: CustomerPolicy;
  readonly selectedRowId = signal<number>(undefined!);
  readonly creditCheckMethods = signal<SelectionItem[]>(undefined!);
  readonly creditLevelChecks = signal<SelectionItem[]>(undefined!);
  readonly customers = signal<SelectionItem[]>(undefined!);
  readonly isNew = signal<boolean>(undefined!);

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['organizationId'].firstChange) {
      this.initialize();
    }
  }

  private initialize(): void {
    const idString: string = this.organizationId();
    if (idString && idString.length > 0) {
      if (!this.personLoginService.isInternalOrRsm()) {
        this.backToOrganizationMaster();
      } else {
        this.organizationService.getOrganization(idString).subscribe(result => {
          let userOrg = this.personLogin.person.primaryOrgUnitId;
          if (this.personLogin.roles.length === 0 && result.orgUnitId !== userOrg && result.controllingOrgId !== userOrg) {
            this.backToOrganizationMaster();
          } else {
            this.selectionService.getCustomers(this.organizationId()).subscribe(result => this.customers.set(result));
            this.loadPolicies();
          }
        });
      }
    } else {
      this.backToOrganizationMaster();
    }
  }

  private loadPolicies(): void {
    this.policyService.getCustomerPolicies(this.organizationId()).subscribe(results => {
      this.policies.set(results);
    });
  }

  public selectPolicy(index: number, policy: CustomerPolicy) {
    this.selectedPolicy = policy;
    this.selectedRowId.set(index);
    this.isNew.set(false);
  }

  private backToOrganizationMaster(): void {
    this.router.navigate(['organization']);
  }

  public backToOrganizationDetail(): void {
    this.router.navigate(['organization/' + this.organizationId()]);
  }

  public save(): void {
    this.selectedPolicy.updateUser = this.personLogin.person.userLogin;
    this.policyService.save(this.selectedPolicy).subscribe(() => {
      this.isNew.set(false);
      this.loadPolicies();
      this.selectedPolicy = null!;
      this.selectedRowId.set(-1);
    });
  }

  public newPolicy(): void {
    this.isNew.set(true);
    this.selectedRowId.set(-1);
    this.selectedPolicy = new CustomerPolicy();
    this.selectedPolicy.customerId = this.customers()[0].code;
    this.selectedPolicy.customerCreditCheckMethodCode = "ORDFCU";
    this.selectedPolicy.defaultCreditLevelCheckingCode = "2";
    this.selectedPolicy.createUser = this.personLogin.person.userLogin;
    this.selectedPolicy.currentBalance = 0;
    this.selectedPolicy.active = true;
    this.selectedPolicy.creditCheckAtContractConfirm = true;
    this.selectedPolicy.orgUnitId = this.organizationId();
  }

  ngOnInit(): void {
    if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']);
    } else {
      this.personLogin = this.personLoginService.getCachedLogin()!;
      this.selectionService.getCreditCheckMethods().subscribe(result => this.creditCheckMethods.set(result));
      this.selectionService.getCreditLevelChecks().subscribe(result => this.creditLevelChecks.set(result));
      this.initialize();
    }
  }

}
